/* eslint-disable indent */
import React, { SVGProps } from "react";
import NodeInfo from "./NodeInfo";
import { useSelector } from "react-redux";
import "./SceneNode.scss";
import { getCts } from "../../../../common/state/slice/contentTypesSlice";
import { fromModelNameGetDescription } from "../../../../common/util/fromModelNameGetDescription";
import { getInteractionIcon } from "./interactionIconMap";
export type AssetType = "image" | "video" | "location" | "sound" | "control" | "model3d" | "effect";

export type Props = SVGProps<SVGSVGElement> & {
  type: AssetType;
  valid?: boolean;
  selected?: boolean;
  disabled?: boolean;
  modelName?: string;
  content?: any;
  ctName?: string;
  hideInfo?: boolean;
};

type Sized = {
  WIDTH: number;
  HEIGHT: number;
};

const SVGNodeAsset: React.FC<Props> & Sized = ({
  type,
  valid,
  selected,
  disabled,
  content,
  modelName,
  ctName,
  hideInfo,
  ...svgProps
}: any) => {
  const x = svgProps.x;
  const y = svgProps.y;
  const strokeColor = disabled ? "rgba(150,150,150,0.5)" : valid ? "#02FCA5" : "rgb(255,0,0)";
  const enableTransparency = Boolean(valid);
  let circleStrokeWidth = valid ? 1.2 : 1;
  const description = fromModelNameGetDescription(useSelector(getCts), modelName);
  const studioIcon = getInteractionIcon(modelName);
  // Icon box centered on the circle center (19,19 within the translated group).
  // ICON_BOX is the base extent in viewBox units; per-icon scale harmonizes
  // apparent size. Drawn larger than the legacy glyph at the bumped node size.
  const ICON_BOX = 22;
  const iconSize = studioIcon ? ICON_BOX * studioIcon.scale : 0;
  const iconOrigin = 19 - iconSize / 2;

  if (selected) circleStrokeWidth += 1.3;
  return (
    <svg
      width={SVGNodeAsset.WIDTH}
      height={SVGNodeAsset.HEIGHT}
      viewBox="0 0 50 50"
      className="thenode"
      {...svgProps}
    >
      {studioIcon && (
        <defs>
          {/* Flatten any studio PNG to a flat brand-cyan (#02FCA5) silhouette
              while keeping its alpha (anti-aliased edges intact). Robust across
              browsers, unlike a CSS `filter` on <image>. Same id on every node
              is fine: the filters are identical.
              Constants = rgb(2,252,165)/255 = 0.00784, 0.98824, 0.64706. */}
          <filter id="interactionIconFlat" colorInterpolationFilters="sRGB">
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0.00784
                      0 0 0 0 0.98824
                      0 0 0 0 0.64706
                      0 0 0 1 0"
            />
          </filter>
        </defs>
      )}
      <g transform="translate(7 7)" fill="none" fillRule="evenodd">
        {!hideInfo && (
          <NodeInfo
            x="48%"
            y="-9"
            type="asset"
            infoText={description?.infoText}
            infoTitle={ctName}
            // Guard the deref: `content` (relevantC) can be undefined when the
            // content row isn't in contentsArr yet. NodeInfo.contentId is optional.
            contentId={content?.id}
            parentX={Number(x)}
            parentY={Number(y)}
            parentHeight={SVGNodeAsset.HEIGHT}
            parentWidth={SVGNodeAsset.WIDTH}
          />
        )}

        <circle
          strokeWidth={circleStrokeWidth}
          fill="#010038"
          cx={19}
          cy={19}
          r={19}
          stroke={strokeColor}
          filter={selected ? "url(#asset_drop_shadow)" : undefined}
        />
        {studioIcon ? (
          <image
            href={studioIcon.url}
            x={iconOrigin}
            y={iconOrigin}
            width={iconSize}
            height={iconSize}
            preserveAspectRatio="xMidYMid meet"
            filter="url(#interactionIconFlat)"
            opacity={enableTransparency ? 0.9 : 1}
          />
        ) : (
          // No studio icon (e.g. hotspots): keep the legacy glyph but force it
          // to the same flat brand-cyan as the studio icons so the graph stays
          // visually uniform. Validity stays readable via the circle stroke.
          getIcon(type, "#02FCA5", enableTransparency)
        )}
      </g>
    </svg>
  );
};

SVGNodeAsset.WIDTH = 94;
SVGNodeAsset.HEIGHT = 94;

//
// ICON
//
// We "build" the Icon in 2 steps:
// - first we wrap it in a group, to set common path options and set transparency when relevant
// - then we get the path(s) composing the icon: some have a fill color, some don't

const getIcon = (type: AssetType, color: string, transparent: boolean) => {
  const opacity = transparent ? 0.7 : 1;
  switch (type) {
    case "image":
    case "video":
      // outline-based icon
      return (
        <g
          strokeLinecap="round"
          strokeLinejoin="round"
          stroke={color}
          strokeWidth={1.5}
          strokeOpacity={opacity}
        >
          {iconPaths[type]}
        </g>
      );
    case "model3d":
      // outline-based icon
      return (
        <g strokeLinecap="round" strokeLinejoin="round" stroke={color} strokeWidth={15}>
          {iconPaths[type]}
        </g>
      );
    case "location":
    case "sound":
    case "control":
    case "effect":
      // shape-based icon
      return (
        <g opacity={opacity} fill={color}>
          {iconPaths[type]}
        </g>
      );
  }
};

const iconPaths = {
  image: [
    <path
      key="outline"
      d="M26.219 10.079H12.097a2.017 2.017 0 00-2.018 2.017v14.122c0 1.114.903 2.018 2.018 2.018h14.122a2.018 2.018 0 002.017-2.018V12.096a2.018 2.018 0 00-2.017-2.017z"
    />,
    <path
      key="strokes"
      d="M15.627 17.14a1.513 1.513 0 100-3.027 1.513 1.513 0 000 3.026zM25.21 21.048l-3.909-3.908-8.599 8.599"
    />,
  ],
  video: (
    <path d="M26.790713,10 L12.05409,10 C10.9196496,10 10,10.9196496 10,12.05409 L10,26.790713 C10,27.9251783 10.9196496,28.8448609 12.05409,28.8448609 L26.790713,28.8448609 C27.9251783,28.8448609 28.8448609,27.9251783 28.8448609,26.790713 L28.8448609,12.05409 C28.8448609,10.9196496 27.9251783,10 26.790713,10 Z M14.711273,10 L14.711273,28.8448609 M24.1338522,10 L24.1338522,28.8448609 M10,19.4223478 L28.8448609,19.4223478 M10,14.7110748 L14.711207,14.7110748 M10,24.1336043 L14.711207,24.1336043 M24.1338522,24.1336043 L28.8450261,24.1336043 M24.1338522,14.7110748 L28.8450261,14.7110748" />
  ),
  location: (
    <path d="M18.945 9C15.105 9 12 12.075 12 15.877c0 4.097 4.385 9.746 6.19 11.898a.991.991 0 001.519 0c1.796-2.152 6.18-7.801 6.18-11.898C25.89 12.075 22.785 9 18.946 9zm0 9.333a2.493 2.493 0 01-1.754-.72 2.444 2.444 0 01-.726-1.736c0-.651.26-1.276.726-1.737a2.493 2.493 0 011.754-.719c.658 0 1.289.259 1.754.72.465.46.726 1.085.726 1.736 0 .652-.261 1.276-.726 1.737-.465.46-1.096.72-1.754.72z" />
  ),
  sound: (
    <path d="M28,24.4527027 C28,25.1297695 27.7310891,25.7786599 27.252913,26.2565128 C26.8194652,26.6894915 26.2464118,26.9496884 25.6403487,26.9934186 L25.457646,27 L24.535157,27 C23.862303,27 23.216714,26.7328206 22.7397892,26.2564121 C22.3051462,25.8220628 22.043304,25.2462929 21.9992885,24.6365405 L21.9926636,24.4527027 L21.9926636,21.6872547 C21.9926636,21.0102327 22.2616819,20.3613296 22.7398901,19.8834446 C23.1733663,19.4504375 23.7463179,19.1902638 24.3524337,19.1465381 L24.535157,19.1399574 L26.6051911,19.1392096 L26.605248,18.0000563 C26.605248,15.9877531 25.8051471,14.0575298 24.3801863,12.6336998 C22.9538366,11.2084822 21.0184507,10.4074243 19.0000139,10.4074243 C16.9815456,10.4074243 15.0461506,11.2084845 13.6198505,12.6336909 C12.2627453,13.9897677 11.4723725,15.8051345 11.4001762,17.7131975 L11.394752,18.0000563 L11.3940875,19.1392096 L13.4649266,19.1399574 C14.137724,19.1399574 14.7834099,19.4071664 15.2602866,19.8835653 C15.6948919,20.317912 15.9567023,20.8936742 16.0007124,21.5034191 L16.0073364,21.6872547 L16.0073364,24.4527027 C16.0073364,25.1297166 15.7383525,25.7786107 15.2601857,26.2564929 C14.8267532,26.6894912 14.2537255,26.9496885 13.6476381,26.9934186 L13.4649266,27 L12.5424098,27 C11.869601,27 11.2239143,26.7327928 10.7470359,26.2563921 C10.3124334,25.8220483 10.0506322,25.2462875 10.0066238,24.6365393 L10,24.4527027 L10,18.0000563 C10,15.6116352 10.949333,13.3214704 12.6384598,11.6336236 C14.3262818,9.94709588 16.6145051,9 19.0000139,9 C21.3854884,9 23.6736959,9.94709099 25.3615614,11.633617 C26.9803238,13.2510946 27.9196076,15.4217707 27.9950709,17.7020442 L28,18.0000563 L28,24.4527027 Z M26.6043748,20.546986 L24.535157,20.5473817 C24.2292869,20.5473817 23.9364538,20.6685718 23.7212228,20.8835697 C23.5378029,21.0668657 23.423571,21.3053871 23.3946766,21.559237 L23.3874156,21.6872547 L23.3874156,24.4527027 C23.3874156,24.7535292 23.507233,25.0425424 23.721122,25.2562869 C23.9056921,25.4406572 24.147202,25.5560334 24.405056,25.5852354 L24.535157,25.5925757 L25.457646,25.5925757 C25.7634349,25.5925757 26.0563905,25.4713444 26.2715802,25.2563877 C26.4549315,25.0731603 26.56911,24.8346734 26.5979905,24.5807604 L26.605248,24.4527027 L26.6043748,20.546986 Z M13.4649266,20.5473817 L11.3941753,20.546986 L11.394752,24.4527027 C11.394752,24.7535625 11.5145518,25.0425773 11.7284086,25.2563069 C11.9129248,25.4406382 12.1544824,25.5560276 12.4123219,25.5852341 L12.5424098,25.5925757 L13.4649266,25.5925757 C13.7707375,25.5925757 14.0636457,25.4713593 14.278813,25.2564076 C14.4622121,25.0731177 14.5764325,24.8345935 14.6053241,24.5807288 L14.6125844,24.4527027 L14.6125844,21.6872547 C14.6125844,21.3864062 14.4927786,21.0973881 14.2789138,20.8836506 C14.0943983,20.6993199 13.852841,20.58393 13.5950097,20.5547233 L13.4649266,20.5473817 Z" />
  ),
  control: (
    <path d="M26.3272326,12.0000001 L11.4420326,12.0000001 C10.2151726,11.9998262 9.00729258,12.3000562 7.92594258,12.8739612 C6.84459258,13.4478612 5.92332258,14.2776312 5.24415258,15.2893912 C4.56498858,16.3011512 4.14899458,17.4635112 4.03321158,18.6729912 C3.91742858,19.8824612 4.10544758,21.1015212 4.58053058,22.2216712 C5.05561258,23.3417712 5.80302258,24.3281712 6.75620258,25.0930712 C7.70939258,25.8579712 8.83878258,26.3775712 10.0438326,26.6056712 C11.2488726,26.8337712 12.4921826,26.7632712 13.6630726,26.4004712 C14.8339326,26.0376712 15.8960326,25.3938712 16.7549326,24.5261712 L21.0111326,24.5261712 C21.8698326,25.3937712 22.9318326,26.0375712 24.1025326,26.4003712 C25.2733326,26.7631712 26.5164326,26.8337712 27.7214326,26.6058712 C28.9263326,26.3778712 30.0556326,25.8584712 31.0088326,25.0937712 C31.9620326,24.3291712 32.7095326,23.3429712 33.1848326,22.2231712 C33.6601326,21.1032412 33.8484326,19.8843612 33.7330326,18.6749912 C33.6175326,17.4656212 33.2019326,16.3032812 32.5232326,15.2914112 C31.8444326,14.2795412 30.9236326,13.4495412 29.8425326,12.8752912 C28.7615326,12.3010432 27.5539326,12.0003582 26.3272326,12.0000001 Z M15.5345326,19.9209912 C15.5345326,20.0675612 15.4757326,20.2081212 15.3711326,20.3117612 C15.2664326,20.4154012 15.1245326,20.4736212 14.9765326,20.4736212 L12.5581826,20.4736212 L12.5581826,22.8683712 C12.5581826,23.0148712 12.4993826,23.1554712 12.3947226,23.2590712 C12.2900626,23.3627712 12.1481226,23.4209712 12.0001126,23.4209712 L10.8839626,23.4209712 C10.7359526,23.4209712 10.5940026,23.3627712 10.4893426,23.2590712 C10.3846826,23.1554712 10.3258926,23.0148712 10.3258926,22.8683712 L10.3258926,20.4736212 L7.90757258,20.4736212 C7.75956258,20.4736212 7.61761258,20.4154012 7.51295258,20.3117612 C7.40830258,20.2081212 7.34950258,20.0675612 7.34950258,19.9209912 L7.34950258,18.8157412 C7.34950258,18.6691712 7.40830258,18.5286112 7.51295258,18.4249712 C7.61761258,18.3213312 7.75956258,18.2631112 7.90757258,18.2631112 L10.3258926,18.2631112 L10.3258926,15.8683912 C10.3258926,15.7218212 10.3846826,15.5812612 10.4893426,15.4776212 C10.5940026,15.3739912 10.7359526,15.3157612 10.8839626,15.3157612 L12.0001126,15.3157612 C12.1481226,15.3157612 12.2900626,15.3739912 12.3947226,15.4776212 C12.4993826,15.5812612 12.5581826,15.7218212 12.5581826,15.8683912 L12.5581826,18.2631112 L14.9765326,18.2631112 C15.1245326,18.2631112 15.2664326,18.3213312 15.3711326,18.4249712 C15.4757326,18.5286112 15.5345326,18.6691712 15.5345326,18.8157412 L15.5345326,19.9209912 Z M25.5798326,23.4209712 C25.2119326,23.4209712 24.8523326,23.3129712 24.5464326,23.1104712 C24.2404326,22.9080712 24.0020326,22.6203712 23.8612326,22.2837712 C23.7204326,21.9471712 23.6836326,21.5768312 23.7553326,21.2195012 C23.8271326,20.8621712 24.0043326,20.5339412 24.2645326,20.2763212 C24.5246326,20.0187012 24.8561326,19.8432512 25.2169326,19.7721812 C25.5778326,19.7011012 25.9518326,19.7375812 26.2917326,19.8770012 C26.6316326,20.0164312 26.9222326,20.2525312 27.1266326,20.5554612 C27.3310326,20.8583912 27.4401326,21.2145412 27.4401326,21.5788712 C27.4401326,22.0674712 27.2441326,22.5359712 26.8952326,22.8814712 C26.5464326,23.2268712 26.0732326,23.4209712 25.5798326,23.4209712 Z M28.5562326,18.9999512 C28.1883326,18.9999512 27.8287326,18.8919112 27.5227326,18.6895012 C27.2168326,18.4870912 26.9784326,18.1993912 26.8376326,17.8627912 C26.6968326,17.5261912 26.6600326,17.1558112 26.7317326,16.7984812 C26.8035326,16.4411512 26.9807326,16.1129212 27.2408326,15.8553012 C27.5010326,15.5976812 27.8325326,15.4222412 28.1933326,15.3511612 C28.5542326,15.2800812 28.9282326,15.3165612 29.2681326,15.4559812 C29.6080326,15.5954112 29.8986326,15.8315112 30.1030326,16.1344412 C30.3074326,16.4373712 30.4165326,16.7935212 30.4165326,17.1578512 C30.4165326,17.6464112 30.2205326,18.1149512 29.8716326,18.4604112 C29.5228326,18.8058712 29.0496326,18.9999512 28.5562326,18.9999512 Z" />
  ),
  model3d: (
    <svg viewBox="-110 -110 800 800">
      <rect width="240" height="240" x="20" y="90" />
      <rect width="240" height="240" x="140" y="30" />
      <path d="M20,90 l120,-60 M260,90 l120,-60 M20,330 l120,-60 M260,330 l120,-60" />
    </svg>
  ),
  effect: (
    <svg
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      width={28}
      height={28}
      x={4}
      y={5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  ),
};

export default SVGNodeAsset;
